/**
 * Layout for the authenticated route tree. Wraps the connections chrome
 * (`ConnectionsAppShell`) around an `<Outlet />` so all authenticated
 * routes share the sidebar, search, and profile/logout controls. Owns
 * the Datadog user-context sync (set on profile change, clear on
 * sign-out).
 *
 * Mounted by the data router as the element of the `/` route, gated by
 * `<RequireAuth>`. Replaces the previous `App.tsx` body — the auth
 * conditional moved into `<RequireAuth>`, the `wasAuthenticated` latch
 * moved into `<AuthSessionProvider>`.
 *
 * @agentic connections
 */

import { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import { useMedplum } from "@medplum/react";
import { notifications } from "@mantine/notifications";
import { Logo } from "@benchmark/ui/components/atomic";
import { CurrentUserProvider } from "@/app/auth/CurrentUserContext";
import { useAuthSession } from "@/app/auth/AuthSessionContext";
import { performLogout } from "@/app/auth/performAuthReset";
import { setDatadogUser, clearDatadogUser } from "@/app/boot/initializeDatadog";
import { useInactivityLogout } from "@/hooks/common/useInactivityLogout";
import { useFeatureFlag } from "@/hooks/shared/useFeatureFlag";
import {
  writeLastActivity,
  readLastActivity,
} from "@/app/auth/lastActivityStorage";
import { dismissBootSplash } from "@/app/boot/bootSplash";
import { AuthenticatedLayoutChrome } from "@/app/routes/AuthenticatedLayoutChrome";

/**
 * Root layout for authenticated users. Builds the sidebar menu and
 * renders the route's `<Outlet />` inside the chrome.
 * @agentic connections
 */
export const AuthenticatedLayout = () => {
  const { profile, resetSession } = useAuthSession();
  const medplum = useMedplum();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    dismissBootSplash();
  }, []);

  useEffect(() => {
    if (profile) {
      const name = profile.name?.[0];
      const displayName = name
        ? [name.given?.join(" "), name.family].filter(Boolean).join(" ")
        : undefined;
      // Clinician `email` is intentionally omitted for data minimization —
      // `id` (+ display name) is enough to correlate a RUM session. `email` is
      // an optional param on `setDatadogUser`; leaving it unset is deliberate,
      // not an oversight.
      setDatadogUser({ id: profile.id ?? "unknown", name: displayName });
    } else {
      clearDatadogUser();
    }
  }, [profile]);

  const inactivityEnabled = useFeatureFlag("inactivityLogout");

  const handleInactivityLogout = useCallback(() => {
    void performLogout("inactivity", {
      signOut: () => medplum.signOut(),
      clearSession: () => medplum.clear(),
      navigate: (to) => {
        const result = navigate(to);
        if (result && typeof (result as Promise<void>).catch === "function") {
          (result as Promise<void>).catch(console.error);
        }
      },
      location,
      resetSession,
      notify: (n) => notifications.show(n),
    });
  }, [medplum, navigate, location, resetSession]);

  const { phase, secondsUntilLogout, extendSession } = useInactivityLogout({
    enabled: inactivityEnabled,
    onLogout: handleInactivityLogout,
    onActivityTick: writeLastActivity,
    getSharedLastActivity: readLastActivity,
  });

  return (
    <CurrentUserProvider>
      <AuthenticatedLayoutChrome
        logo={<Logo size={140} variant="standard" color="light" />}
        inactivityWarning={{
          opened: phase === "warning",
          secondsUntilLogout,
          onExtendSession: extendSession,
        }}
      />
    </CurrentUserProvider>
  );
};
